import React, { useMemo, useRef, useState } from 'react';
import { getLocale, shallowEqual, useSelector } from 'umi';

import Filter from '@/pages/dashboard/components/NewFilter';
import { getDateVal, setBtnPointLog } from "@/pages/dashboard/utils/utils";
import { renderMetric } from '../comparison/filterList';
import { useFilterData } from './hooks';
import { eventPageFilterDefualt } from './eventPageFilterDefualt';
import { EventSelectorFilter } from './components/EventSelectorFilter';
import { getDateDefault } from '../../utils/dateUtils';
import { useFrontMetricsOverride } from '@/pages/dashboard/hooks/useFrontMetricsList';

/**
 *
 * @param props
 * @param props:filterConfirm 筛选器确认函数
 * @param props:filterReset 筛选器重置函数
 * @param props:setFilterLoading 筛选器loading设置，透传到筛选器组件内部，获取到筛选器列表时区等数据之后，设置为false，测试才可进行页面和组件中的接口调用
 * @param props:filterInit 初始化函数，会向外部传递初始化后的筛选器值
 * @returns
 */

// 筛选器路径
const FILTER_PATH = 'eventAnalysis/eventAnalysis';

const eventInit = {
  type: 'eventType',
  event: ['255'],
  eventType: []
};
const EventFilter = (props: any) => {
  const { filterConfirm, filterReset, buttonLogConfig, setFilterLoading, path = FILTER_PATH, filterInit, openSticky = false, children, isHaveFilterSave, timeZone = 0, config } = props;
  // daily筛选器继承逻辑
  const filterValueRef = useRef<any>(null); // 筛选器值，此处不用state，更新formValue同样会触发组件更新
  const { seasonInfo, metricsInfo } = useSelector((state: any) => ({
    // 初始可能为undefied,在此加兜底
    seasonInfo: state?.dashboardSetting?.gameConfig?.season_info || { date: [], week: [], month: [] },
    metricsInfo: state.dashboard?.metricsInfo || {},
  }), shallowEqual);
  const isEN = getLocale() === 'en-US';

  const { loading, selector, eventOptions, eventLoading, eventError, getEventListFunc } = useFilterData(path, 'daily', eventPageFilterDefualt, true);
  const { mergedSelector } = useFrontMetricsOverride(config, selector, metricsInfo);
  const { latestDate } = mergedSelector || {};

  const [eventFilterVal, setEventFilterVal] = useState(eventInit);
  // 设置inputList
  const [inputList] = useState([
    {
      key: 'metrics',
      type: "metrics",
      isUseConfig: true,
      // visible: 1,
      default: ['active_users', 'new_users'],
      render: renderMetric
    },
    {
      type: 'rangeDaily',
      key: 'rangeTime',
      default: getDateDefault({ timeType: 'day', timeZone, startNum: 14, endNum: 1 }),
      extraShortcut: seasonInfo // isPcInflexion ? seasonInfo : undefined
    },
  ]);

  const filterItems = (baseArr: any[], targetTypeArr: any[], targetArr: string | any[]) => {
    let events: any[] = [];

    const eventTypeOptionsArr = baseArr?.filter(item => 
      // 实际当前后端只有name_en有值
      targetTypeArr?.includes(item?.name_en) || targetTypeArr?.includes(item?.name_cn)
    ) || [];

    eventTypeOptionsArr.forEach(item => {
      const records = item.event_record.filter((record: { event_title_en: string; event_title_cn: string; }) =>
        targetArr.includes(record.event_title_en) || targetArr.includes(record.event_title_cn)
      );
      events = events.concat(records);
    });

    return events;
  };

  const eventVal = useMemo(() => {
    // 这样写原因：1、后端event没有唯一id，要求唯一id前端自己处理写死
    // 2、后端要求去重
    // 3、之所以把去重逻辑写在这里而不是组件中的onchange，是因为要在这里对value进行处理，
    // 删掉前端添加的唯一id相关逻辑,在组件中处理，会影响选中态，处理完之后的value和组件渲染的时候value不一致
    
    const eventResultArr = eventFilterVal?.event?.map(item => item?.split('_event_name_value_')?.[0]);
    // 需要给后端传对应的中英文
    const resultsArr = filterItems(eventOptions,eventFilterVal?.eventType,eventResultArr)?.map(item=>isEN ? item?.event_title_en : item?.event_title_cn) || [];
    return {
      type: eventFilterVal?.type,
      event: resultsArr,
      eventType: eventFilterVal?.eventType || []
    };
  }, [eventFilterVal, eventOptions, isEN]);

  const handleConfirm = (value: any) => {
    filterConfirm?.({
      ...value,
      rangeTime: [
        getDateVal(value?.rangeTime?.[0]),
        getDateVal(value?.rangeTime?.[1]),
      ],
      timeType: 'Day',
      event: eventVal,
      selector: mergedSelector,
      pagePath: path,
      filterDefaultSelect: eventPageFilterDefualt
    });
    filterValueRef.current = { ...value, event: eventInit };
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, value);
  };

  const handleReset = (value: any) => {
    filterReset?.({
      ...value,
      rangeTime: [
        getDateVal(value?.rangeTime?.[0]),
        getDateVal(value?.rangeTime?.[1]),
      ],
      timeType: 'Day',
      event: eventInit,
      selector: mergedSelector,
      pagePath: path,
      filterDefaultSelect: eventPageFilterDefualt
    });
    filterValueRef.current = { ...value, event: eventInit };
    getEventListFunc(getDateVal(value?.rangeTime?.[0]), getDateVal(value?.rangeTime?.[1]));
    setEventFilterVal(eventInit);
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, value, true);
  };

  const handleInit = (value: any) => {
    const startTime = getDateVal(value?.rangeTime?.[0]);
    const endTime = getDateVal(value?.rangeTime?.[1]);
    filterInit?.({
      ...value,
      rangeTime: [
        getDateVal(startTime),
        getDateVal(endTime),
      ],
      timeType: 'Day',
      event: eventInit,
      selector: mergedSelector,
      pagePath: path,
      filterDefaultSelect: eventPageFilterDefualt
    });
    filterValueRef.current = { ...value, event: eventInit };
    getEventListFunc(startTime, endTime);
    setFilterLoading(false);
  };

  const onChange = (key: string, value: any) => {
    if (key === 'rangeTime') {
      // 重新获取数据前清空选中额值
      setEventFilterVal(eventInit);
      const startTime = getDateVal(value?.[0]);
      const endTime = getDateVal(value?.[1]);
      getEventListFunc(startTime, endTime);
      filterValueRef.current = {
        ...filterValueRef.current,
        rangeTime: value,
        event: eventInit
      };
    } else {
      filterValueRef.current = {
        ...filterValueRef.current,
        [key]: value
      };
    }
  };

  return (
    <Filter
      openSticky={openSticky}
      pageDefaultSelector={eventPageFilterDefualt}
      useFrontFilterConfig={true}
      extraRenderContent={<>
        <EventSelectorFilter
          options={eventOptions}
          eventLoading={eventLoading}
          eventError={eventError}
          onChange={setEventFilterVal}
          value={eventFilterVal} />
      </>}
      path={path}
      value={filterValueRef.current}
      latestDateType={''}
      outLatestDate={latestDate}
      inputList={inputList}
      onInit={handleInit}
      onConfirm={handleConfirm}
      onReset={handleReset}
      outSelector={mergedSelector}
      loading={loading}
      // isOutScheme={true}
      onChange={onChange}
      isHaveFilterSave={isHaveFilterSave}
    >
      {children}
    </Filter>
  );
};

export default EventFilter;
